import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  OutcomeReason,
  PositionStage,
  SubmissionStatus,
  isTerminalStatus,
  proposeClosingDecisions,
  type ClosingDecision,
  type OutcomeReason as OutcomeReasonCode,
  type PositionStage as PositionStageCode,
  type SetPositionStage,
  type SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { api } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { usePosition, useInvalidatePositions } from './queries';
import type { PositionSubmission, StageResponse } from './types';

const KEEP = '__keep__';

interface RowDecision {
  status: SubmissionStatusCode | null;
  outcomeReason: OutcomeReasonCode | null;
}

export interface ClosingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positionId: string | null;
  stage: PositionStageCode | null;
  /** Proposals returned by a 409 `DECISIONS_REQUIRED`; computed from the position when absent. */
  decisions?: ClosingDecision[];
  onDone?: (result: StageResponse) => void;
}

function candidateLabel(s: PositionSubmission): string {
  return s.candidate.label ?? s.candidate.fullName ?? s.candidate.candidateId;
}

/**
 * Lost/Dropped dialog (spec §6.1): lists the open submissions with the proposed decisions.
 * Screening → Rejected "Client closed the role" is pre-selected; anything past Submitted defaults
 * to "Keep as is" and can be closed individually with a status + outcome reason.
 */
export function ClosingDialog({
  open,
  onOpenChange,
  positionId,
  stage,
  decisions,
  onDone,
}: ClosingDialogProps) {
  const position = usePosition(positionId, open);
  const invalidate = useInvalidatePositions();
  const [reason, setReason] = React.useState('');
  const [rows, setRows] = React.useState<Record<string, RowDecision>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [initialisedFor, setInitialisedFor] = React.useState<string | null>(null);

  const openSubmissions = React.useMemo(
    () => (position.data?.submissions ?? []).filter((s) => !isTerminalStatus(s.status)),
    [position.data],
  );
  const proposals = React.useMemo<ClosingDecision[]>(() => {
    const computed = proposeClosingDecisions(openSubmissions.map((s) => ({ id: s.id, status: s.status })));
    if (!decisions) return computed;
    // Prefer the server's proposal when it knows about the submission.
    return computed.map((c) => decisions.find((d) => d.submissionId === c.submissionId) ?? c);
  }, [openSubmissions, decisions]);

  // Initialise the rows once per opening, during render rather than in an effect: an effect runs
  // after the first paint, which showed every submission as "Keep as is" for one frame.
  const initKey = open && position.data ? `${positionId}:${position.data.id}` : null;
  if (initKey !== initialisedFor) {
    setInitialisedFor(initKey);
    if (initKey) {
      setReason('');
      setErrors({});
      const next: Record<string, RowDecision> = {};
      for (const p of proposals) {
        next[p.submissionId] = p.requiresDecision
          ? { status: null, outcomeReason: null }
          : { status: p.proposedStatus, outcomeReason: p.proposedReason };
      }
      setRows(next);
    }
  }

  const mutation = useMutation({
    mutationFn: (body: SetPositionStage) =>
      api.post<StageResponse>(
        `/positions/${encodeURIComponent(position.data?.id ?? positionId ?? '')}/stage`,
        body,
      ),
    onSuccess: async (result) => {
      toast.success(`Stage → ${stage ? PositionStage.label(stage) : ''}`.trim());
      await invalidate();
      onDone?.(result);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!stage) return;
    const nextErrors: Record<string, string> = {};
    const submissionDecisions = openSubmissions.map((s) => {
      const row = rows[s.id] ?? { status: null, outcomeReason: null };
      if (row.status && isTerminalStatus(row.status) && !row.outcomeReason) {
        nextErrors[s.id] = 'An outcome reason is required for Rejected, Withdrawn and Placed.';
      }
      return { submissionId: s.id, status: row.status, outcomeReason: row.outcomeReason ?? undefined };
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    mutation.mutate({
      stage,
      reason: reason.trim() || undefined,
      submissionDecisions: submissionDecisions.length > 0 ? submissionDecisions : undefined,
    });
  };

  const stageLabel = stage ? PositionStage.label(stage) : '';
  const statusOptions = [{ value: KEEP, label: 'Keep as is' }, ...enumOptions(SubmissionStatus)];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Move to {stageLabel}</DialogTitle>
            <DialogDescription>
              {position.data ? `${position.data.requestNo} · ${position.data.role}. ` : ''}
              Open submissions need a decision. Screening ones are proposed as Rejected (Client closed the
              role); candidates already in the client process are never closed automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="closing-reason">Reason (optional)</Label>
            <Input
              id="closing-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client filled the role internally"
              maxLength={500}
            />
          </div>

          {position.isLoading ? (
            <SkeletonLines lines={3} />
          ) : position.error ? (
            <p role="alert" className="text-sm text-destructive">
              {position.error.message}
            </p>
          ) : openSubmissions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open submissions – nothing else to decide.</p>
          ) : (
            <ul className="divide-y rounded-lg border" aria-label="Open submissions">
              {openSubmissions.map((s) => {
                const row = rows[s.id] ?? { status: null, outcomeReason: null };
                const proposal = proposals.find((p) => p.submissionId === s.id);
                const label = candidateLabel(s);
                const needsReason = Boolean(row.status && isTerminalStatus(row.status));
                return (
                  <li key={s.id} className="space-y-2 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{label}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {s.candidate.candidateId}
                      </span>
                      <EnumBadge def={SubmissionStatus} value={s.status} size="sm" />
                    </div>
                    {proposal ? <p className="text-xs text-muted-foreground">{proposal.note}</p> : null}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor={`decision-${s.id}`} className="text-xs text-muted-foreground">
                          Decision
                        </Label>
                        <SimpleSelect
                          id={`decision-${s.id}`}
                          aria-label={`Decision for ${label}`}
                          value={row.status ?? KEEP}
                          options={statusOptions}
                          onChange={(v) => {
                            const status = v && v !== KEEP ? (v as SubmissionStatusCode) : null;
                            setRows((r) => ({
                              ...r,
                              [s.id]: {
                                status,
                                outcomeReason:
                                  status && isTerminalStatus(status)
                                    ? (r[s.id]?.outcomeReason ??
                                      (status === 'REJECTED' ? 'CLIENT_CLOSED_ROLE' : null))
                                    : null,
                              },
                            }));
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`reason-${s.id}`} className="text-xs text-muted-foreground">
                          Outcome reason
                        </Label>
                        <SimpleSelect
                          id={`reason-${s.id}`}
                          aria-label={`Outcome reason for ${label}`}
                          value={row.outcomeReason}
                          options={enumOptions(OutcomeReason)}
                          allowEmpty
                          disabled={!needsReason}
                          placeholder={needsReason ? 'Select…' : '–'}
                          onChange={(v) =>
                            setRows((r) => ({
                              ...r,
                              [s.id]: {
                                status: r[s.id]?.status ?? null,
                                outcomeReason: (v as OutcomeReasonCode) ?? null,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                    {errors[s.id] ? (
                      <p role="alert" className="text-xs text-destructive">
                        {errors[s.id]}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={stage === 'LOST' || stage === 'DROPPED' ? 'destructive' : 'default'}
              loading={mutation.isPending}
              disabled={position.isLoading || Boolean(position.error)}
            >
              Move to {stageLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
